import * as THREE from 'three';
import { LOW, cachedTexture } from '../core/tier.js';
import { markDynamic } from '../world/freezeStatics.js';

const UP = new THREE.Vector3(0, 1, 0);
const _p0 = new THREE.Vector3();
const _p1 = new THREE.Vector3();
const _p2 = new THREE.Vector3();
const _orbPos = new THREE.Vector3();
const _tailPos = new THREE.Vector3();
const _sparkPos = new THREE.Vector3();
const _trailDir = new THREE.Vector3();
const _trailMid = new THREE.Vector3();

const COLORS = [0x8efcff, 0xd59bff, 0xffe66d, 0x94ff9d];
const CHARGE_END = 0.18;
const LAUNCH_END = 0.68;
const SPELL_LIFE = LOW ? 1.05 : 1.25;
const SPARK_COUNT = LOW ? 10 : 18;

function makeGlowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.28, 'rgba(255,255,255,0.72)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const glowTex = cachedTexture('wizard-spell-glow', makeGlowTexture);

function setBezier(out, a, b, c, t) {
  const it = 1 - t;
  out.set(
    a.x * it * it + b.x * 2 * it * t + c.x * t * t,
    a.y * it * it + b.y * 2 * it * t + c.y * t * t,
    a.z * it * it + b.z * 2 * it * t + c.z * t * t
  );
}

function placeTrail(mesh, from, to, opacity) {
  _trailDir.subVectors(to, from);
  const len = _trailDir.length();
  if (len < 0.02 || opacity <= 0.01) {
    mesh.visible = false;
    return;
  }
  mesh.visible = true;
  _trailMid.addVectors(from, to).multiplyScalar(0.5);
  mesh.position.copy(_trailMid);
  mesh.quaternion.setFromUnitVectors(UP, _trailDir.multiplyScalar(1 / len));
  mesh.scale.set(1, len, 1);
  mesh.material.opacity = opacity;
}

export function createWizardSpellFx({ parent, x, z, groundY, getYaw }) {
  const root = new THREE.Group();
  markDynamic(root);   // the cast animates everything inside
  root.visible = false;
  parent.add(root);

  const orbMat = new THREE.SpriteMaterial({
    map: glowTex,
    color: COLORS[0],
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: true,
  });
  const orb = new THREE.Sprite(orbMat);
  root.add(orb);

  const coreMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: true,
  });
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.075, LOW ? 6 : 8, LOW ? 4 : 6),
    coreMat
  );
  root.add(core);

  const trailMat = new THREE.MeshBasicMaterial({
    color: COLORS[0],
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: true,
  });
  const trail = new THREE.Mesh(
    new THREE.CylinderGeometry(0.025, 0.05, 1, 6, 1, true),
    trailMat
  );
  root.add(trail);

  const burstMat = new THREE.SpriteMaterial({
    map: glowTex,
    color: COLORS[0],
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: true,
  });
  const burst = new THREE.Sprite(burstMat);
  burst.visible = false;
  root.add(burst);

  const sparkPositions = new Float32Array(SPARK_COUNT * 3);
  const sparkDirs = new Float32Array(SPARK_COUNT * 3);
  const sparkSpeeds = new Float32Array(SPARK_COUNT);
  const sparkPhases = new Float32Array(SPARK_COUNT);
  const sparkGeo = new THREE.BufferGeometry();
  const sparkAttr = new THREE.BufferAttribute(sparkPositions, 3);
  sparkAttr.setUsage(THREE.DynamicDrawUsage);
  sparkGeo.setAttribute('position', sparkAttr);
  const sparkMat = new THREE.PointsMaterial({
    map: glowTex,
    color: COLORS[0],
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: true,
    size: LOW ? 0.18 : 0.16,
    sizeAttenuation: true,
  });
  const sparks = new THREE.Points(sparkGeo, sparkMat);
  sparks.frustumCulled = false;
  root.add(sparks);

  const spell = {
    active: false,
    age: 0,
    nextAt: 2.5 + Math.random() * 3.5,
    side: 1,
    color: COLORS[0],
  };

  function randomizeSparks() {
    for (let i = 0; i < SPARK_COUNT; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 0.35 + Math.random() * 0.75;
      const j = i * 3;
      sparkDirs[j] = Math.cos(a) * r;
      sparkDirs[j + 1] = 0.25 + Math.random() * 0.95;
      sparkDirs[j + 2] = Math.sin(a) * r;
      sparkSpeeds[i] = 0.55 + Math.random() * 1.0;
      sparkPhases[i] = Math.random() * Math.PI * 2;
    }
  }

  function refreshAnchors() {
    const yaw = getYaw();
    const forwardX = Math.sin(yaw);
    const forwardZ = Math.cos(yaw);
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    const handSide = spell.side;
    const drift = Math.sin(spell.nextAt * 2.7) * 0.28;

    _p0.set(
      x + rightX * handSide * 0.34 + forwardX * 0.12,
      groundY + 1.34,
      z + rightZ * handSide * 0.34 + forwardZ * 0.12
    );
    _p2.set(
      x + forwardX * (0.75 + Math.abs(drift) * 0.5) + rightX * (handSide * 0.18 + drift),
      groundY + 2.65 + Math.abs(drift) * 0.5,
      z + forwardZ * (0.75 + Math.abs(drift) * 0.5) + rightZ * (handSide * 0.18 + drift)
    );
    _p1.addVectors(_p0, _p2).multiplyScalar(0.5);
    _p1.y += 0.65;
  }

  function start(t) {
    spell.active = true;
    spell.age = 0;
    spell.side = Math.random() < 0.5 ? -1 : 1;
    spell.color = COLORS[(Math.random() * COLORS.length) | 0];
    spell.nextAt = t + 5.5 + Math.random() * 4.5;
    orbMat.color.setHex(spell.color);
    trailMat.color.setHex(spell.color);
    burstMat.color.setHex(spell.color);
    sparkMat.color.setHex(spell.color);
    randomizeSparks();
    refreshAnchors();
    root.visible = true;
    burst.visible = false;
  }

  function hide() {
    spell.active = false;
    root.visible = false;
    orbMat.opacity = 0;
    coreMat.opacity = 0;
    trail.visible = false;
    trailMat.opacity = 0;
    burst.visible = false;
    burstMat.opacity = 0;
    sparkMat.opacity = 0;
  }

  function updateCharge(t, p) {
    refreshAnchors();
    const k = p / CHARGE_END;
    const pulse = 0.5 + Math.sin(t * 22) * 0.5;
    orb.position.copy(_p0);
    core.position.copy(_p0);
    const s = 0.22 + k * 0.22 + pulse * 0.04;
    orb.scale.set(s, s, 1);
    core.scale.setScalar(0.7 + pulse * 0.35);
    orbMat.opacity = 0.8 * k;
    coreMat.opacity = 0.55 * k;
    trail.visible = false;
    burst.visible = false;
    sparkMat.opacity = 0.55 * k;

    for (let i = 0; i < SPARK_COUNT; i++) {
      const j = i * 3;
      const phase = sparkPhases[i] + t * (2.4 + (i % 3) * 0.5);
      const r = 0.08 + 0.1 * k;
      sparkPositions[j] = _p0.x + Math.cos(phase) * r;
      sparkPositions[j + 1] = _p0.y + Math.sin(phase * 1.3) * r;
      sparkPositions[j + 2] = _p0.z + Math.sin(phase) * r;
    }
    sparkGeo.attributes.position.needsUpdate = true;
  }

  function updateLaunch(t, p) {
    const k = (p - CHARGE_END) / (LAUNCH_END - CHARGE_END);
    refreshAnchors();
    setBezier(_orbPos, _p0, _p1, _p2, k);
    orb.position.copy(_orbPos);
    core.position.copy(_orbPos);
    const pulse = 0.5 + Math.sin(t * 28) * 0.5;
    const s = 0.32 + pulse * 0.08;
    orb.scale.set(s, s, 1);
    core.scale.setScalar(0.85 + pulse * 0.3);
    orbMat.opacity = 0.92;
    coreMat.opacity = 0.7;

    const tailT = Math.max(0, k - 0.23);
    setBezier(_tailPos, _p0, _p1, _p2, tailT);
    placeTrail(trail, _tailPos, _orbPos, 0.45 + pulse * 0.2);
    burst.visible = false;
    sparkMat.opacity = 0.65;

    for (let i = 0; i < SPARK_COUNT; i++) {
      const j = i * 3;
      const trailK = Math.max(0, k - (i / SPARK_COUNT) * 0.2);
      setBezier(_sparkPos, _p0, _p1, _p2, trailK);
      const phase = sparkPhases[i] + t * 5;
      const wobble = 0.08 + (i % 4) * 0.012;
      sparkPositions[j] = _sparkPos.x + Math.cos(phase) * wobble;
      sparkPositions[j + 1] = _sparkPos.y + Math.sin(phase * 1.2) * wobble;
      sparkPositions[j + 2] = _sparkPos.z + Math.sin(phase) * wobble;
    }
    sparkGeo.attributes.position.needsUpdate = true;
  }

  function updateBurst(t, p) {
    const k = (p - LAUNCH_END) / (1 - LAUNCH_END);
    refreshAnchors();
    const fade = Math.max(0, 1 - k);
    orb.position.copy(_p2);
    core.position.copy(_p2);
    const orbS = 0.4 + k * 0.35;
    orb.scale.set(orbS, orbS, 1);
    core.scale.setScalar(0.8 + k * 0.8);
    orbMat.opacity = 0.65 * fade;
    coreMat.opacity = 0.45 * fade;
    placeTrail(trail, _p0, _p2, 0.22 * fade);

    burst.visible = true;
    burst.position.copy(_p2);
    const burstS = 0.35 + k * 1.25;
    burst.scale.set(burstS, burstS, 1);
    burstMat.opacity = 0.7 * fade;
    sparkMat.opacity = 0.9 * fade;

    for (let i = 0; i < SPARK_COUNT; i++) {
      const j = i * 3;
      const dist = sparkSpeeds[i] * (0.25 + k * 0.95);
      const swirl = Math.sin(t * 5 + sparkPhases[i]) * 0.08 * fade;
      sparkPositions[j] = _p2.x + sparkDirs[j] * dist + swirl;
      sparkPositions[j + 1] = _p2.y + sparkDirs[j + 1] * dist - k * k * 0.45;
      sparkPositions[j + 2] = _p2.z + sparkDirs[j + 2] * dist - swirl;
    }
    sparkGeo.attributes.position.needsUpdate = true;
  }

  function update(dt, t, awake = true) {
    if (!awake) {
      if (spell.active) hide();
      else root.visible = false;
      return;
    }
    if (!spell.active) {
      if (t >= spell.nextAt) start(t);
      return;
    }

    spell.age += dt;
    const p = spell.age / SPELL_LIFE;
    if (p >= 1) {
      hide();
      return;
    }

    root.visible = true;
    if (p < CHARGE_END) updateCharge(t, p);
    else if (p < LAUNCH_END) updateLaunch(t, p);
    else updateBurst(t, p);
  }

  return { update };
}
